/**
 * Canonical audit-log writer (Task #21).
 *
 * Single source of truth for client-side audit_logs writes. Encapsulates
 * the canonical `AuditLogEntry` shape from `src/types/index.ts:789`:
 *
 *     { action, module, actorId, actorName?, targetId?, targetName?,
 *       details?, ipAddress?, userAgent?, status, createdAt }
 *
 * Pre-Task-#21 the codebase had two client-side writers that drifted:
 *   - `src/hooks/useAuditLog.ts` — canonical but inlined the field map.
 *   - `src/features/auth/AuthProvider.tsx` (login-failure path) — used
 *      legacy field names (`performedBy`, `targetResource`) and an
 *      object-typed `details`. This helper standardizes both.
 *
 * IMPORTANT: per Task #6 the firestore.rules `audit_logs/create` rule is
 * `allow create: if false`, i.e. CLIENT-SIDE WRITES ARE REJECTED by
 * design. This helper exists for three reasons anyway:
 *
 *   1. **Shape consistency.** When a client writer DOES attempt a log
 *      (typically a failed-login or similar pre-auth path) the doc it
 *      tries to write should still be canonical, so when (a) we move
 *      that path through a callable in the future, or (b) the rule is
 *      relaxed for a specific narrow case, the shape is already right.
 *
 *   2. **Helper for future callable-backed audit writes.** A typed,
 *      pre-validated shape catches mistakes at the type-checker layer
 *      rather than at runtime in production.
 *
 *   3. **Single import point.** Any future code path that needs to
 *      write an audit entry imports `writeAuditLog` from here.
 *
 * Server-side audit writes (Cloud Functions using the admin SDK — see
 * `functions/src/awardPoints.ts`, `createChildAccount`, `subscriptionAdmin`,
 * `completeChallenge`, `sendTestNotification`) already use the canonical
 * shape natively; they don't import this helper because they run under
 * a different package boundary.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

/**
 * Input shape for `writeAuditLog`. Mirrors `AuditLogEntry` from
 * `src/types/index.ts` minus the server-stamped `id` and `createdAt`.
 * `details` is stringified by callers (JSON or plain text) to match the
 * canonical-type contract (`details?: string`).
 */
export interface AuditLogInput {
    /** e.g. 'user.update', 'login.failure', 'class.delete'. */
    action: string;
    /** e.g. 'users', 'finance', 'auth'. */
    module: string;
    /** UID of the user (or 'system') performing the action. */
    actorId: string;
    /** Optional display name of the actor. */
    actorName?: string;
    /** Optional id of the affected object. */
    targetId?: string;
    /** Optional human-readable label of the affected object. */
    targetName?: string;
    /** Optional summary string. Stringify objects before passing. */
    details?: string;
    /** Optional originating IP. Usually only available server-side. */
    ipAddress?: string;
    /** Optional User-Agent. Defaulted to `navigator.userAgent` when omitted in a browser. */
    userAgent?: string;
    /** Required outcome flag. Defaults to 'success' for ergonomic callers. */
    status?: 'success' | 'failure';
}

/**
 * Write a canonical audit-log entry to Firestore via `addDoc`.
 *
 * - `createdAt` is stamped server-side with `serverTimestamp()` (NOT
 *   `new Date()`, which is clock-skew-prone and was the legacy bug).
 * - `userAgent` defaults to `navigator.userAgent` when the helper runs
 *   in a browser environment.
 * - `status` defaults to `'success'`.
 * - Errors are RE-THROWN. Callers that want fire-and-forget behavior
 *   should wrap in try/catch (see `useAuditLog.logAction` and the
 *   AuthProvider login-failure path for examples).
 */
export async function writeAuditLog(
    db: Firestore,
    input: AuditLogInput,
): Promise<void> {
    const defaultUserAgent =
        typeof navigator !== 'undefined' && navigator.userAgent
            ? navigator.userAgent
            : undefined;

    await addDoc(collection(db, 'audit_logs'), {
        action: input.action,
        module: input.module,
        actorId: input.actorId,
        actorName: input.actorName ?? null,
        targetId: input.targetId ?? null,
        targetName: input.targetName ?? null,
        details: input.details ?? null,
        ipAddress: input.ipAddress ?? null,
        userAgent: input.userAgent ?? defaultUserAgent ?? null,
        status: input.status ?? 'success',
        createdAt: serverTimestamp(),
    });
}
